import React, { useState } from 'react';
import {
  Download,
  Info,
  RefreshCw,
  Settings,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import PeerScoringDetailDrawer from '../components/PeerScoringDetailDrawer';
import { usePeerReviewCorrection } from '../data/peerReviewCorrectionStore';

// 互评任务管理页面
// 页面结构：条件筛选卡片 + 结果卡片 + 生成互评任务页面 + 清空分配页面 + 查看明细页面 + 二级抽屉页面
export default function PeerReviewTasksPage() {
  // ================= 页面状态 =================
  const [isGenerateDrawerOpen, setIsGenerateDrawerOpen] = useState(false);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [isScoreDetailDrawerOpen, setIsScoreDetailDrawerOpen] = useState(false);
  const [clearModalType, setClearModalType] = useState('');
  const [isWangQiangMalicious, setIsWangQiangMalicious] = usePeerReviewCorrection();

  // ================= 互评任务主表数据 =================
  const peerTaskList = [
    {
      id: 1,
      academicYear: '2025-2026',
      semester: '第一学期',
      batch: '2025秋季批次',
      college: '计算机学院',
      className: '计算机1班',
      classSize: 50,
      progress: '45 / 50',
      assignBase: '约15人',
      maliciousCount: isWangQiangMalicious ? 1 : 0,
      generateStatus: '已生成',
      hasScoreRecord: true,
    },
    {
      id: 2,
      academicYear: '2025-2026',
      semester: '第一学期',
      batch: '2025秋季批次',
      college: '计算机学院',
      className: '计算机2班',
      classSize: 48,
      progress: '0 / 48',
      assignBase: '-',
      maliciousCount: 0,
      generateStatus: '未生成',
      hasScoreRecord: false,
    },
  ];

  return (
    <div className="animate-fade-in space-y-4">
      {/* 条件筛选卡片：按批次、学院、班级名称、学年学期和生成状态查询互评任务 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        <div className="flex flex-wrap items-end gap-x-7 gap-y-4">
          <FilterSelect
            label="学年"
            options={['全部', '2025-2026', '2024-2025']}
          />

          <FilterSelect
            label="学期"
            options={['全部', '第一学期', '第二学期']}
          />

          <FilterSelect
            label="批次"
            options={['全部', '2025秋季批次', '2025春季批次']}
          />

          <FilterSelect
            label="学院"
            options={['全部', '计算机学院', '经济学院']}
          />

          <FilterInput
            label="班级名称"
            placeholder="请输入班级名称"
          />

          <FilterSelect
            label="状态"
            options={['全部', '已生成', '未生成']}
          />

          <div className="flex-1" />

          {/* 查询按钮区 */}
          <div className="flex gap-3">
            <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
              重置
            </button>
            <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
              查询
            </button>
          </div>
        </div>
      </div>

      {/* 结果卡片：展示班级互评任务生成状态和互评进度 */}
      <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
        {/* 结果卡片顶部操作区 */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
          <button
            onClick={() => setIsGenerateDrawerOpen(true)}
            className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]"
          >
            生成互评任务
          </button>
        </div>

        {/* 表格上方提示信息：展示互评任务处理口径 */}
        <div className="mb-5">
          <InfoBanner text="班级调整或人工处理异常评分后，可进入明细查看学生互评完成情况和恶意打分修正结果。" />
        </div>

        {/* 互评任务结果表格 */}
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap min-w-max">
            <thead>
              <tr className="bg-[#fafafa] border-b border-gray-200">
                <th className="py-3 px-4 w-10">
                  <input type="checkbox" className="rounded border-gray-300" />
                </th>
                <TableHead>序号</TableHead>
                <TableHead>学年</TableHead>
                <TableHead>学期</TableHead>
                <TableHead>批次</TableHead>
                <TableHead>学院</TableHead>
                <TableHead>班级</TableHead>
                <TableHead>班级人数</TableHead>
                <TableHead>互评进度（已完成/总人数）</TableHead>
                <TableHead>分配基准(约30%)</TableHead>
                <TableHead>恶意打分人数</TableHead>
                <TableHead>是否已生成</TableHead>
                <th className="py-3 px-4 font-medium text-gray-800 sticky right-0 bg-[#fafafa] z-10 border-l border-gray-200">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {peerTaskList.map((row, index) => (
                <tr
                  key={row.id}
                  className="border-b border-gray-100 hover:bg-[#f8f9fa] group"
                >
                  <td className="py-3 px-4">
                    <input type="checkbox" className="rounded border-gray-300" />
                  </td>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.academicYear}</TableCell>
                  <TableCell>{row.semester}</TableCell>
                  <TableCell>{row.batch}</TableCell>
                  <TableCell>{row.college}</TableCell>
                  <TableCell>{row.className}</TableCell>
                  <TableCell>{row.classSize}</TableCell>
                  <TableCell>
                    <span className="font-bold text-gray-900">
                      {row.progress}
                    </span>
                  </TableCell>
                  <TableCell>{row.assignBase}</TableCell>
                  <TableCell>
                    <span className={row.maliciousCount > 0 ? 'text-red-500 font-bold' : ''}>
                      {row.maliciousCount}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusTag status={row.generateStatus} />
                  </TableCell>
                  <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-[#f8f9fa] z-10 border-l border-gray-100">
                    <div className="flex items-center gap-4">
                      {row.generateStatus === '已生成' ? (
                        <button
                          onClick={() => setIsDetailDrawerOpen(true)}
                          className="text-[#1890ff] hover:underline"
                        >
                          查看明细
                        </button>
                      ) : (
                        <span className="text-gray-400">
                          暂无明细
                        </span>
                      )}

                      <button
                        onClick={() => setClearModalType(row.hasScoreRecord ? 'hasRecord' : 'empty')}
                        className="text-[#1890ff] hover:underline"
                      >
                        清空分配
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 分页区 */}
        <SimplePagination total={peerTaskList.length} />
      </div>

      {/* 右侧页面：生成互评任务 */}
      {isGenerateDrawerOpen && (
        <GeneratePeerReviewTaskDrawer onClose={() => setIsGenerateDrawerOpen(false)} />
      )}

      {/* 右侧页面：查看班级互评打分情况明细 */}
      {isDetailDrawerOpen && (
        <PeerReviewDetailDrawer
          onClose={() => setIsDetailDrawerOpen(false)}
          onOpenScoreDetail={() => setIsScoreDetailDrawerOpen(true)}
          isWangQiangMalicious={isWangQiangMalicious}
        />
      )}

      {/* 右侧二级页面：查看学生打分明细 */}
      {isScoreDetailDrawerOpen && (
        <PeerScoringDetailDrawer
          onClose={() => setIsScoreDetailDrawerOpen(false)}
          showReceived
          title="打分明细查看"
          isMaliciousOverride={isWangQiangMalicious}
          onMaliciousChange={setIsWangQiangMalicious}
        />
      )}

      {/* 小弹窗：清空分配提示 */}
      {clearModalType && (
        <ClearAssignModal
          type={clearModalType}
          onClose={() => setClearModalType('')}
        />
      )}
    </div>
  );
}

// 查看明细页面
function PeerReviewDetailDrawer({
  onClose,
  onOpenScoreDetail,
  isWangQiangMalicious,
}) {
  // ================= 明细表格数据 =================
  const detailList = [
    {
      id: 1,
      batch: '2025秋季批次',
      studentId: '2102001',
      name: '陈红',
      className: '计算机1班',
      finalScore: '4.80',
      classSize: 15,
      reviewedCount: '15 / 15',
      coveredRate: '15 / 15',
      lowScoreCount: 1,
      lowScoreRate: '6.67%',
      maliciousStatus: '否',
    },
    {
      id: 2,
      batch: '2025秋季批次',
      studentId: '2102003',
      name: '张伟',
      className: '计算机1班',
      finalScore: '4.20',
      classSize: 5,
      reviewedCount: '5 / 5',
      coveredRate: '12 / 14',
      lowScoreCount: 0,
      lowScoreRate: '0%',
      maliciousStatus: '否',
    },
    {
      id: 3,
      batch: '2025秋季批次',
      studentId: '2102045',
      name: '王强',
      className: '计算机1班',
      finalScore: '4.60',
      classSize: 15,
      reviewedCount: '15 / 15',
      coveredRate: '15 / 15',
      lowScoreCount: 12,
      lowScoreRate: '80%',
      maliciousStatus: isWangQiangMalicious ? '是' : '否',
    },
  ];

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/35">
      <div
        className="h-full bg-[#f3f6fa] shadow-[-8px_0_24px_rgba(0,0,0,0.18)] flex flex-col"
        style={{ width: 'min(1260px, calc(100vw - 320px))' }}
      >
        {/* 抽屉标题栏 */}
        <DrawerHeader
          title="互评打分情况明细 - 计算机1班"
          onClose={onClose}
        />

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* 条件筛选卡片：单独放置，方便产品经理调整查询项 */}
          <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
            <div className="flex flex-wrap items-end gap-x-7 gap-y-4">
              <FilterInput
                label="学号"
                placeholder="请输入学号"
              />

              <FilterInput
                label="姓名"
                placeholder="请输入姓名"
              />

              <FilterSelect
                label="是否计入恶意打分"
                options={['全部', '是', '否']}
              />

              <FilterSelect
                label="打分比例是否超过阈值"
                options={['全部', '是', '否']}
              />

              <FilterSelect
                label="是否辅导员恶意打分修正"
                options={['全部', '是', '否']}
              />

              <div className="flex-1" />

              <div className="flex gap-3">
                <button className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50">
                  重置
                </button>
                <button className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
                  查询
                </button>
              </div>
            </div>
          </div>

          {/* 结果展示卡片：提示信息、导出按钮、互评打分情况明细表格 */}
          <div className="bg-white rounded shadow-sm border border-gray-100 p-5">
            {/* 表格上方提示信息：面向非技术人员说明统计口径 */}
            <div className="mb-4">
              <InfoBanner text="这里展示每位学生的互评完成情况。若学生被标记为恶意打分，只会取消他给别人打出的分数，不会扣减他自己收到的互评分。" />
            </div>

            {/* 表格上方操作按钮 */}
            <div className="flex justify-between items-center mb-4">
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm flex items-center gap-1 hover:bg-gray-50">
                <Download size={15} />
                导出
              </button>

              <TableToolbar />
            </div>

            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap min-w-max">
                <thead>
                  <tr className="bg-[#fafafa] border-b border-gray-200">
                    <TableHead>批次</TableHead>
                    <TableHead>学号</TableHead>
                    <TableHead>姓名</TableHead>
                    <TableHead>班级</TableHead>
                    <TableHead>最终互评分</TableHead>
                    <TableHead>班级人数</TableHead>
                    <TableHead>已评价学生(已评/分配)</TableHead>
                    <TableHead>被评覆盖率(被评/分配)</TableHead>
                    <TableHead>打出低分数量</TableHead>
                    <TableHead>打出低分比例</TableHead>
                    <TableHead>计入恶意打分</TableHead>
                    <th className="py-3 px-4 font-medium text-gray-800 sticky right-0 bg-[#fafafa] z-10 border-l border-gray-200">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detailList.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 hover:bg-[#f8f9fa] group"
                    >
                      <TableCell>{row.batch}</TableCell>
                      <TableCell>{row.studentId}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.className}</TableCell>
                      <TableCell>
                        <span className="text-[#1890ff] font-bold">
                          {row.finalScore}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-bold text-gray-900">
                          {row.classSize}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-green-600">
                          {row.reviewedCount}
                        </span>
                      </TableCell>
                      <TableCell>{row.coveredRate}</TableCell>
                      <TableCell>{row.lowScoreCount}</TableCell>
                      <TableCell>{row.lowScoreRate}</TableCell>
                      <TableCell>{row.maliciousStatus}</TableCell>
                      <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-[#f8f9fa] z-10 border-l border-gray-100">
                        <button
                          onClick={() => onOpenScoreDetail('giver')}
                          className="text-[#1890ff] hover:underline"
                        >
                          查看打分明细
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <SimplePagination total={detailList.length} />
          </div>
        </div>
      </div>
    </div>
  );
}

// 生成互评任务页面
function GeneratePeerReviewTaskDrawer({ onClose }) {
  // ================= 即将生成任务的班级数据 =================
  const generateClassList = [
    {
      id: 1,
      batch: '2025秋季批次',
      college: '计算机学院',
      className: '计算机2班',
      classSize: 48,
    },
  ];

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/35">
      <div
        className="h-full bg-[#f3f6fa] shadow-[-8px_0_24px_rgba(0,0,0,0.18)] flex flex-col"
        style={{ width: 'min(720px, calc((100vw - 240px) * 0.52))' }}
      >
        {/* 抽屉标题栏 */}
        <div className="h-[58px] px-6 bg-white border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-1 h-5 bg-[#1890ff] rounded" />
            <h2 className="text-[16px] font-bold text-gray-900">
              确认生成学生互评任务
            </h2>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* 顶部提示信息：展示本次生成任务采用的参数配置 */}
        <div className="px-6 py-4 bg-[#eaf4ff] border-b border-[#d3e8ff] text-sm text-[#1677ff] leading-7">
          <div className="flex gap-2">
            <Info size={16} className="mt-1 shrink-0" />
            <div>
              <div>
                生成任务将以当前【评分参数配置】的设定为基准。
              </div>
              <div className="font-bold">
                当前生效配置：互评人数比例 30%｜低分判定 ≤1分｜恶意打分判定：低分比例 &gt; 50%｜剔除极值各 1 个
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* 即将生成任务的班级卡片 */}
          <div className="bg-white rounded shadow-sm border border-gray-100 p-6">
            <h3 className="text-[16px] font-bold text-gray-900 mb-4">
              即将生成任务的班级（1）
            </h3>

            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="w-full text-left border-collapse text-[13px] text-gray-700 whitespace-nowrap">
                <thead>
                  <tr className="bg-[#fafafa] border-b border-gray-200">
                    <TableHead>序号</TableHead>
                    <TableHead>批次</TableHead>
                    <TableHead>学院</TableHead>
                    <TableHead>班级</TableHead>
                    <TableHead>班级人数</TableHead>
                  </tr>
                </thead>
                <tbody>
                  {generateClassList.map((row, index) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 hover:bg-[#f8f9fa]"
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.batch}</TableCell>
                      <TableCell>{row.college}</TableCell>
                      <TableCell>{row.className}</TableCell>
                      <TableCell>
                        <span className="font-bold text-gray-900">
                          {row.classSize}
                        </span>
                      </TableCell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 抽屉底部按钮区 */}
        <div className="h-[64px] px-6 bg-white border-t border-gray-200 flex justify-end items-center gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
          >
            取消
          </button>
          <button className="px-6 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]">
            确定生成任务
          </button>
        </div>
      </div>
    </div>
  );
}

// 小弹窗：清空分配确认和禁止清空提示
function ClearAssignModal({ type, onClose }) {
  const scoreCount = type === 'hasRecord' ? 45 : 0;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/35">
      <div className="w-[460px] bg-white rounded shadow-[0_8px_28px_rgba(0,0,0,0.18)]">
        <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-[15px] font-bold text-gray-900">
            提示
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* 当前班级打分信息：让操作人明确会影响多少评分记录 */}
        <div className="mx-6 mt-5 px-3 py-3 bg-[#f7f9fc] border border-gray-200 rounded text-sm text-gray-700 leading-6">
          当前班级已提交打分人数：
          <span className="mx-1 font-bold text-[#1890ff]">
            {scoreCount}
          </span>
          人。
        </div>

        {/* 固定提示信息：清空分配会同时清除评分记录，需醒目标红展示 */}
        <div className="mx-6 my-4 px-3 py-3 bg-red-50 border border-red-200 rounded text-sm text-red-600 font-bold leading-6">
          清空分配会移除当前班级已生成的互评关系且清除全部评分记录，确认是否清空？
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
          >
            取消
          </button>

          <button
            onClick={onClose}
            className="px-5 py-2 bg-[#1890ff] text-white rounded text-sm hover:bg-[#096dd9]"
          >
            确定
          </button>
        </div>
      </div>
    </div>
  );
}

// 抽屉标题栏
function DrawerHeader({ title, onClose }) {
  return (
    <div className="h-[58px] px-6 bg-white border-b border-gray-200 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="w-1 h-5 bg-[#1890ff] rounded" />
        <h2 className="text-[16px] font-bold text-gray-900">
          {title}
        </h2>
      </div>

      <button
        onClick={onClose}
        className="text-gray-400 hover:text-gray-600"
      >
        <X size={20} />
      </button>
    </div>
  );
}

// 条件筛选：下拉框
function FilterSelect({ label, options }) {
  return (
    <label className="block">
      <span className="block mb-2 text-sm text-gray-700">
        {label}
      </span>
      <select className="w-[170px] border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#1890ff] bg-white text-gray-700">
        {options.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

// 条件筛选：输入框
function FilterInput({ label, placeholder }) {
  return (
    <label className="block">
      <span className="block mb-2 text-sm text-gray-700">
        {label}
      </span>
      <input
        placeholder={placeholder}
        className="w-[220px] border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-[#1890ff]"
      />
    </label>
  );
}

// 信息提示条
function InfoBanner({ text }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-2 bg-[#eaf4ff] border border-[#d3e8ff] rounded text-xs text-[#1677ff]">
      <Info size={15} />
      <span>{text}</span>
    </div>
  );
}

// 表格工具按钮
function TableToolbar() {
  return (
    <div className="flex items-center gap-4 text-gray-700">
      <button className="hover:text-[#1890ff]" title="刷新">
        <RefreshCw size={18} />
      </button>
      <button className="hover:text-[#1890ff]" title="密度">
        <SlidersHorizontal size={18} />
      </button>
      <button className="hover:text-[#1890ff]" title="设置">
        <Settings size={18} />
      </button>
    </div>
  );
}

// 表格表头
function TableHead({ children }) {
  return (
    <th className="py-3 px-4 font-medium text-gray-800">
      {children}
    </th>
  );
}

// 表格单元格
function TableCell({ children }) {
  return (
    <td className="py-3 px-4">
      {children}
    </td>
  );
}

// 状态标签
function StatusTag({ status }) {
  const isGenerated = status === '已生成';

  return (
    <span className={isGenerated ? 'px-2 py-1 rounded border border-green-200 bg-green-50 text-green-600 text-xs' : 'px-2 py-1 rounded border border-gray-200 bg-gray-50 text-gray-500 text-xs'}>
      {status}
    </span>
  );
}

// 简单分页：用于展示当前页、总条数、每页条数
function SimplePagination({ total }) {
  return (
    <div className="flex justify-end items-center mt-4 text-sm text-gray-700 gap-2">
      <span>
        共
        <span className="mx-1 font-bold">{total}</span>
        条记录
      </span>
      <button className="text-gray-300 px-2 py-1 border border-gray-200 rounded">
        &lt;
      </button>
      <button className="px-2.5 py-1 border border-[#1890ff] bg-[#1890ff] text-white rounded">
        1
      </button>
      <button className="text-gray-500 px-2 py-1 border border-gray-200 rounded">
        &gt;
      </button>
      <span>前往</span>
      <input
        defaultValue="1"
        className="w-12 border border-gray-300 rounded px-2 py-1 text-center outline-none"
      />
      <span>页</span>
    </div>
  );
}
